import { ViewConfig } from '../../../models/view-config.interface';
import { CarrierFoldersPaginatedService } from '@app/carrier/services/carrier-folders-paginated.service';
import { getCurrentOrganizationSiteKey } from '@app/stores/appStore';
import { CarrierViewActionsService } from '../carrier-view-actions.service';
import { ColumnCreator } from '../column-creator';

export interface SharePermissionsBundlesConfigDeps {
    carrierFoldersPaginatedService: CarrierFoldersPaginatedService;
    actionsService: CarrierViewActionsService;
}

export class SharePermissionsBundlesConfig {
    static create(deps: SharePermissionsBundlesConfigDeps): ViewConfig {
        return {
            id: 'share-permissions-bundles',
            name: 'Bundles',
            translationKey: 'OUTGOING-GOODS.TOGGLER.FOLDERS',
            type: 'folders',
            userRole: 'carrier',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey = getCurrentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve();
                    }
                    // Use the paginated service that loads data into stores (same as regular carrier config)
                    return deps.carrierFoldersPaginatedService.loadCarrierFoldersPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select(),
                    ColumnCreator.bundleNumber(),
                    ColumnCreator.bundleName(),
                    ColumnCreator.consignorName({
                        header: 'SPEDITEUR.TABLE.HEADERS.CONSIGNOR',
                    }),
                    ColumnCreator.carrierName({
                        header: 'SPEDITEUR.TABLE.HEADERS.CARRIER',
                    }),
                    ColumnCreator.consigneeName({
                        header: 'SPEDITEUR.TABLE.HEADERS.CONSIGNEE',
                    }),
                    ColumnCreator.dateShipped({
                        header: 'SPEDITEUR.TABLE.HEADERS.SENT-DATE',
                    }),
                    ColumnCreator.status({
                        header: 'SPEDITEUR.TABLE.HEADERS.STATUS',
                    }),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: false, // No pagination for share permissions dialog
                    selectable: true,
                    expandable: false, // No expansion for share permissions
                },
            },
            filterConfig: {
                filterKeys: [
                    {
                        keyName: 'bundleNumber',
                        label: 'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
                        translationKey:
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
                    },
                    {
                        keyName: 'status',
                        label: 'SPEDITEUR.TABLE.HEADERS.STATUS',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.STATUS',
                    },
                    {
                        keyName: 'consignorName',
                        label: 'SPEDITEUR.TABLE.HEADERS.CONSIGNOR',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.CONSIGNOR',
                    },
                    {
                        keyName: 'consigneeName',
                        label: 'SPEDITEUR.TABLE.HEADERS.CONSIGNEE',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.CONSIGNEE',
                    },
                    {
                        keyName: 'carrierName',
                        label: 'SPEDITEUR.TABLE.HEADERS.CARRIER',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.CARRIER',
                    },
                ],
                showClosed: false,
                enableAdvancedFilters: false,
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'bundleNumber',
                    'status',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            permissions: {
                canCreate: false,
                canUpdate: false,
                canDelete: false,
                canExport: false,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
        };
    }
}
